import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, FileText, Printer, Download, User, Droplets, 
  AlertTriangle, Pill, Activity, Scissors, Stethoscope, 
  Building2, CreditCard, Plus, Trash2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';
import { ProtectedFeaturePage } from '@/components/ProtectedFeaturePage';

/**
 * DoctorSummaryPage - Summary to show doctors with PDF export
 * PREMIUM FEATURE - Requires Premium subscription
 */

const DoctorSummaryContent = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getMedicalProfile, getAllergies, getMedications, getConditions, getSurgeries, getDoctors, getInsurance } = useDemo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    fetchData();
  }, [profileId, isDemoMode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Demo mode: build summary from local demo data
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const medicalProfile = getMedicalProfile() || {};
        const allergies = getAllergies() || [];
        const medications = getMedications() || [];
        const conditions = getConditions() || [];
        const surgeries = getSurgeries() || [];
        const doctors = getDoctors() || [];
        const insurance = getInsurance() || [];
        
        const demoSummary = {
          profile_name: demoProfile?.name || 'Demo User',
          full_name: medicalProfile.full_name || demoProfile?.name || 'Demo User',
          date_of_birth: medicalProfile.date_of_birth || '',
          blood_type: medicalProfile.blood_type || '',
          allergies: allergies.map(a => ({ name: a.name, severity: a.severity, reaction: a.reaction })),
          medications: medications.map(m => ({ name: m.name, dosage: m.dosage, frequency: m.frequency, reason: m.reason || m.purpose })),
          conditions: conditions.map(c => ({ condition: c.condition || c.name, diagnosed_date: c.diagnosed_date })),
          surgeries: surgeries.map(s => ({ name: s.name || s.procedure, date: s.date, hospital: s.hospital || s.facility })),
          doctors: doctors.map(d => ({ name: d.name, specialty: d.specialty, phone: d.phone, clinic: d.clinic || d.practice })),
          pharmacies: [], // Not in demo data
          insurance: insurance.map(i => ({ provider: i.provider, policy_number: i.policy_number, group_number: i.group_number })),
          doctor_questions: []
        };
        
        setSummary(demoSummary);
        setQuestions([]);
        setLoading(false);
        return;
      }
      
      const response = await API.get(`/emergency-binder/profiles/${profileId}/doctor-summary`);
      setSummary(response.data);
      setQuestions(response.data.doctor_questions || []);
    } catch (error) {
      console.error('Failed to fetch doctor summary:', error);
      if (!isDemoMode) {
        toast.error('Failed to load summary');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuestions = async () => {
    try {
      setSaving(true);
      
      // Demo mode: just show success (questions stored locally in state)
      if (isDemoMode) {
        toast.success('Questions saved (demo)');
        setSaving(false);
        return;
      }
      
      await API.put(`/emergency-binder/profiles/${profileId}`, {
        doctor_questions: questions.filter(q => q.trim())
      });
      toast.success('Questions saved');
    } catch (error) {
      if (!isDemoMode) {
        toast.error('Failed to save questions');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const addQuestion = () => {
    setQuestions([...questions, '']);
  };

  const updateQuestion = (index, value) => {
    setQuestions(questions.map((q, i) => i === index ? value : q));
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const Section = ({ icon: Icon, title, color, children }) => (
    <div className="mb-6 print:mb-4">
      <div className="flex items-center gap-2 mb-3 print:mb-2">
        <Icon className="h-5 w-5 print:h-4 print:w-4" style={{ color }} />
        <h3 className="font-semibold text-slate-800 print:text-sm">{title}</h3>
      </div>
      <div className="pl-7 print:pl-6 text-slate-600 print:text-sm">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-24 print:bg-white print:pb-0" data-testid="doctor-summary-page">
      {/* Header - Hidden on print */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40 print:hidden">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/emergency-binder')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Doctor Visit Summary</h1>
              <p className="text-xs text-slate-500">{summary?.profile_name}</p>
            </div>
            <Button variant="outline" onClick={handlePrint} className="gap-2">
              <Printer className="h-4 w-4" />
              Print
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 print:px-8 print:py-4 print:max-w-none">
        {/* Printable Summary Card */}
        <Card className="border-0 shadow-sm print:shadow-none print:border print:border-slate-200">
          <CardHeader className="print:pb-2">
            <CardTitle className="text-xl print:text-lg flex items-center gap-2">
              <FileText className="h-6 w-6 text-blue-500 print:h-5 print:w-5" />
              Medical Summary for Doctor Visit
            </CardTitle>
            <p className="text-sm text-slate-500 print:text-xs">
              Generated on {new Date().toLocaleDateString()}
            </p>
          </CardHeader>
          <CardContent className="space-y-1">
            {/* Patient Info */}
            <Section icon={User} title="Patient Information" color="#3B82F6">
              <div className="grid grid-cols-2 gap-2 print:gap-1">
                <div>
                  <span className="text-slate-500">Name:</span>{' '}
                  <strong>{summary?.full_name || summary?.profile_name || '—'}</strong>
                </div>
                <div>
                  <span className="text-slate-500">DOB:</span>{' '}
                  <strong>{summary?.date_of_birth || '—'}</strong>
                </div>
                <div>
                  <span className="text-slate-500">Blood Type:</span>{' '}
                  <strong className="text-red-600">{summary?.blood_type || '—'}</strong>
                </div>
              </div>
            </Section>

            {/* Allergies */}
            <Section icon={AlertTriangle} title="Allergies" color="#F59E0B">
              {summary?.allergies && summary.allergies.length > 0 ? (
                <ul className="list-disc list-inside">
                  {summary.allergies.map((a, i) => (
                    <li key={i} className="text-amber-700 font-medium">
                      {a.name} {a.severity && `(${a.severity})`}
                      {a.reaction && <span className="text-slate-500"> — {a.reaction}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic">No known allergies</p>
              )}
            </Section>

            {/* Medications */}
            <Section icon={Pill} title="Current Medications" color="#3B82F6">
              {summary?.medications && summary.medications.length > 0 ? (
                <ul className="list-disc list-inside">
                  {summary.medications.map((m, i) => (
                    <li key={i}>
                      <strong>{m.name}</strong>
                      {m.dosage && ` ${m.dosage}`}
                      {m.frequency && ` — ${m.frequency}`}
                      {m.reason && <span className="text-slate-500"> (for {m.reason})</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic">No current medications</p>
              )}
            </Section>

            {/* Conditions */}
            <Section icon={Activity} title="Medical Conditions" color="#8B5CF6">
              {summary?.conditions && summary.conditions.length > 0 ? (
                <ul className="list-disc list-inside">
                  {summary.conditions.map((c, i) => (
                    <li key={i}>
                      <strong>{c.condition}</strong>
                      {c.diagnosed_date && <span className="text-slate-500"> — diagnosed {c.diagnosed_date}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic">No conditions on file</p>
              )}
            </Section>

            {/* Surgeries */}
            <Section icon={Scissors} title="Surgeries & Procedures" color="#EC4899">
              {summary?.surgeries && summary.surgeries.length > 0 ? (
                <ul className="list-disc list-inside">
                  {summary.surgeries.map((s, i) => (
                    <li key={i}>
                      <strong>{s.name}</strong>
                      {s.date && <span className="text-slate-500"> — {s.date}</span>}
                      {s.hospital && <span className="text-slate-500"> at {s.hospital}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic">No surgeries on file</p>
              )}
            </Section>

            {/* Doctors */}
            <Section icon={Stethoscope} title="Doctors" color="#22C55E">
              {summary?.doctors && summary.doctors.length > 0 ? (
                <ul className="list-disc list-inside">
                  {summary.doctors.map((d, i) => (
                    <li key={i}>
                      <strong>{d.name}</strong>
                      {d.specialty && ` (${d.specialty})`}
                      {d.clinic && <span className="text-slate-500"> — {d.clinic}</span>}
                      {d.phone && <span className="text-slate-500"> — {d.phone}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic">No doctors on file</p>
              )}
            </Section>

            {/* Pharmacy */}
            <Section icon={Building2} title="Pharmacy" color="#06B6D4">
              {summary?.pharmacies && summary.pharmacies.length > 0 ? (
                <ul className="list-disc list-inside">
                  {summary.pharmacies.map((p, i) => (
                    <li key={i}>
                      <strong>{p.name}</strong>
                      {p.phone && <span className="text-slate-500"> — {p.phone}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic">No pharmacy on file</p>
              )}
            </Section>

            {/* Insurance */}
            <Section icon={CreditCard} title="Insurance" color="#6366F1">
              {summary?.insurance && summary.insurance.length > 0 ? (
                <ul className="list-disc list-inside">
                  {summary.insurance.map((ins, i) => (
                    <li key={i}>
                      <strong>{ins.provider}</strong>
                      {ins.policy_number && <span className="text-slate-500"> — Policy: {ins.policy_number}</span>}
                      {ins.group_number && <span className="text-slate-500">, Group: {ins.group_number}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="italic">No insurance on file</p>
              )}
            </Section>
          </CardContent>
        </Card>

        {/* Questions for Doctor - Hidden on print if empty */}
        <Card className="border-0 shadow-sm mt-6 print:hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Questions for My Doctor</CardTitle>
              <Button variant="outline" size="sm" onClick={addQuestion}>
                <Plus className="h-4 w-4 mr-1" />
                Add Question
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {questions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                Add questions to discuss with your doctor
              </p>
            ) : (
              questions.map((q, index) => (
                <div key={index} className="flex gap-2">
                  <Textarea
                    value={q}
                    onChange={(e) => updateQuestion(index, e.target.value)}
                    placeholder="Type your question..."
                    rows={2}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500"
                    onClick={() => removeQuestion(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
            {questions.length > 0 && (
              <Button
                onClick={handleSaveQuestions}
                disabled={saving}
                className="w-full"
                variant="outline"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Questions
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Print Buttons - Hidden on print */}
        <div className="flex gap-3 mt-6 print:hidden">
          <Button onClick={handlePrint} className="flex-1 h-12 bg-blue-600 hover:bg-blue-700">
            <Printer className="h-4 w-4 mr-2" />
            Print Summary
          </Button>
          <Button variant="outline" className="flex-1 h-12" onClick={() => navigate('/emergency-binder')}>
            Back to Binder
          </Button>
        </div>
      </main>
    </div>
  );
};

// Wrap content with feature protection
const DoctorSummaryPage = () => (
  <ProtectedFeaturePage
    feature="doctor_summary"
    title="Doctor Summary"
    description="Get a comprehensive medical summary to share with healthcare providers. Perfect for appointments and emergencies."
    backPath="/emergency-binder"
  >
    <DoctorSummaryContent />
  </ProtectedFeaturePage>
);

export default DoctorSummaryPage;
